/* Shared look for labelled form controls (TextField, NumberField, TextArea, Select, Combobox). */
.field {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  min-inline-size: 0;
}

.labelRow {
  display: flex;
  gap: var(--space-2);
  align-items: baseline;
}

.label {
  color: var(--color-text);
  font-size: var(--text-sm);
  font-weight: var(--weight-medium);
  line-height: var(--leading-tight);
}

.optional {
  color: var(--color-text-subtle);
  font-size: var(--text-xs);
  font-weight: var(--weight-regular);
}

.hiddenLabel {
  position: absolute;
  inline-size: 1px;
  block-size: 1px;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
}

.description {
  max-inline-size: var(--prose-max);
  color: var(--color-text-muted);
  font-size: var(--text-sm);
  line-height: var(--leading-normal);
}

.error {
  display: flex;
  gap: var(--space-1);
  align-items: flex-start;
  color: var(--color-danger);
  font-size: var(--text-sm);
  line-height: var(--leading-normal);
}

.error > svg {
  margin-block-start: calc(var(--space-1) / 2);
}

/* The input frame: a control plus optional prefix/suffix adornments (units). */
.control {
  display: flex;
  align-items: center;
  inline-size: 100%;
  min-block-size: var(--control-md);
  border: var(--border-width) solid var(--color-border-strong);
  border-radius: var(--radius-md);
  background: var(--color-surface);
  color: var(--color-text);
  transition: border-color var(--duration-fast) ease;
}

.control:focus-within {
  border-color: var(--color-accent);
  outline: var(--focus-ring);
  outline-offset: calc(var(--border-width) * -1);
}

.control[data-invalid],
.field[data-invalid] .control {
  border-color: var(--color-danger);
}

.control[data-disabled],
.field[data-disabled] .control {
  border-color: var(--color-border);
  background: var(--color-surface-sunken);
  color: var(--color-text-subtle);
}

.control[data-size="sm"] {
  min-block-size: var(--control-sm);
}

.control[data-size="lg"] {
  min-block-size: var(--control-lg);
}

.input {
  flex: 1;
  min-inline-size: 0;
  block-size: 100%;
  min-block-size: inherit;
  padding: 0 var(--space-2);
  border: 0;
  background: transparent;
  color: inherit;
  font-size: var(--text-md);
  line-height: var(--leading-tight);
  outline: none;
}

.input:focus-visible {
  outline: none;
}

.input[data-numeric] {
  text-align: end;
  font-variant-numeric: var(--numeric);
}

.input[data-mono] {
  font-family: var(--font-mono);
  letter-spacing: var(--tracking-wide);
}

.textarea {
  padding: var(--space-2);
  line-height: var(--leading-normal);
  resize: vertical;
}

.adornment {
  display: inline-flex;
  flex: none;
  align-items: center;
  padding-inline: var(--space-2);
  color: var(--color-text-subtle);
  font-size: var(--text-sm);
  white-space: nowrap;
}

.adornment:first-child {
  padding-inline-end: 0;
}

.adornment:last-child {
  padding-inline-start: 0;
}

.stepper {
  display: inline-flex;
  flex: none;
  align-items: center;
  justify-content: center;
  inline-size: var(--control-sm);
  align-self: stretch;
  color: var(--color-text-muted);
}

@media (hover: hover) and (pointer: fine) {
  .stepper:hover:not([data-disabled]) {
    background: var(--color-surface-hover);
    color: var(--color-text);
  }
}

.stepper[data-disabled] {
  color: var(--color-text-subtle);
}

/* For a wrapper element whose children should lay out as if they were the field's own. */
.contents {
  display: contents;
}
